import "./style.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { ModalContainer } from "@amzn/stencil-react-components/dist/submodules/modal/ModalContainer";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { MessageStruct } from "common/types/ui-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FunctionComponent, useState } from "react";

import { MessageBanner } from "../MessageBanners";

const STRING_PATH_PREFIX = "licenseReturn.submitReturnModal.";

export interface SubmitReturnModalProps {
  /**
   * Function to close the modal
   */
  close: () => void;

  /**
   * Name of the product being returned
   */
  productName?: string;

  /**
   * Function to handle the return confirmation
   */
  onConfirm: () => Promise<void>;

  /**
   * Optional messages to display (e.g., errors)
   */
  messages?: MessageStruct[];

  /**
   * Whether the return process is currently loading
   */
  isLoading?: boolean;
}

/**
 * Modal component for confirming asset returns
 */
export const SubmitReturnModal: FunctionComponent<SubmitReturnModalProps> = ({
  close,
  productName,
  onConfirm,
  messages,
  isLoading = false,
}: SubmitReturnModalProps) => {
  const [localMessages, setLocalMessages] = useState<MessageStruct[]>(messages || []);
  const [isProcessing, setIsProcessing] = useState<boolean>(isLoading);

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      await onConfirm();

      close();
    } catch (error: any) {
      const tempMessages: MessageStruct[] = [...localMessages];
      tempMessages.push({
        type: MessageBannerType.Error,
        message: error.message || "An error occurred while processing your return request.",
      });
      setLocalMessages(tempMessages);
    } finally {
      setIsProcessing(false);
    }
  };

  const productText = productName ? `${productName}` : "this tool";

  return (
    <div id="return-modal-container">
      <ModalContent
        titleText={getString(`${STRING_PATH_PREFIX}title`)}
        maxWidth={"555px"}
        buttons={[
          <Button
            key="return-cancel"
            variant={ButtonVariant.Secondary}
            onClick={close}
            disabled={isProcessing}
            data-testid="cancel-return-button"
          >
            {getString(`${STRING_PATH_PREFIX}cancel`)}
          </Button>,
          <Button
            key="return-confirm"
            variant={ButtonVariant.Primary}
            onClick={handleConfirm}
            disabled={isProcessing}
            data-testid="confirm-return-button"
          >
            {getString(`${STRING_PATH_PREFIX}submit`)}
            {isProcessing && <span className="loading-indicator"></span>}
          </Button>,
        ]}
      >
        {localMessages && localMessages.length > 0 && (
          <div className="submit-return-modal__messages">
            {localMessages.map((message, index) => (
              <MessageBanner
                key={index}
                id={`return-message-${index}`}
                testId={`return-message-${index}`}
                type={message.type}
                message={message.message}
              />
            ))}
          </div>
        )}

        <div className="submit-return-modal__message">
          <Text fontSize="T200" fontWeight={"regular"}>
            {getString(`${STRING_PATH_PREFIX}description`, { productText: productText })}
          </Text>
        </div>
      </ModalContent>
    </div>
  );
};
